import { Trans, t } from "@lingui/macro";
import { Edit2, Play, Plus, Trash2, Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import Page from "../../const/Page";
import { Soribot } from "../../models/Soribot";
import { SoribotTestCase, TestQuestion } from "../../models/SoribotTestCase";
import { Fire } from "../../services/FireClient";
import { UtilApp } from "../../services/Utils/UtilApp";
import { TestCaseExportModal, TestCaseImportModal } from "./compontents/TestCaseExportImportModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ScrollArea } from "../../components/ui/scroll-area";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

var SelectedTestCase: SoribotTestCase | undefined; // Must be global so that the subscribe closure can access it.

const SoribotTestCases: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [inProgress, setInProgress] = useState(false);
  const [testCases, setTestCases] = useState<SoribotTestCase[]>([]);
  const [selectedTestCase, setSelectedTestCase] = useState<SoribotTestCase>();
  const [question, setQuestion] = useState('');
  const history = useHistory();
  const { toast } = useToast();

  // Dialog states
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean, title: string, tc?: SoribotTestCase }>({ open: false, title: '' });
  const [editQuestionDialog, setEditQuestionDialog] = useState<{ open: boolean, question: string, qObj?: TestQuestion }>({ open: false, question: '' });
  const [alert, setAlert] = useState<{ open: boolean, title: string, message: React.ReactNode, confirm: (() => void) | null }>({ open: false, title: '', message: '', confirm: null });

  // Input states for dialogs
  const [newTitle, setNewTitle] = useState('');

  useEffect(() => {
    setInProgress(true);
    SelectedTestCase = undefined;
    return Fire.bot.subscribeToTestCases(bot.id, list => {
      setTestCases(list);
      setSelectedTestCase(prev => SelectedTestCase ?? list[0] ?? undefined);
      setInProgress(false);
    });
  }, [bot])

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)] gap-6">
      <div className="lg:w-1/3 border rounded bg-white flex flex-col">
        {inProgress && <div className="p-4 flex justify-center"><Loader2 className="animate-spin text-gray-500" /></div>}
        <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
          <h5 className="font-semibold text-sm"><Trans>Test cases</Trans></h5>
          <Button size="sm" variant="outline" onClick={() => { setNewTitle(''); setAddDialog(true); }}><Plus className="w-4 h-4 mr-1" /><Trans>Add</Trans></Button>
        </div>
        <ScrollArea className="flex-1">
          {testCases.length === 0 && !inProgress && <div className="p-4 text-center text-gray-500 text-sm"><Trans>Press Add to create a new test case.</Trans></div>}
          <div className="divide-y">
            {testCases.map(tc => <TestCaseItem key={tc.id} tc={tc} />)}
          </div>
        </ScrollArea>
      </div>

      <div className="lg:w-2/3 border rounded bg-white overflow-hidden flex flex-col">
        {selectedTestCase ? (
          <div className="h-full flex flex-col">
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 overflow-hidden flex-1">
                  <h5 className="font-semibold text-lg truncate">{selectedTestCase.title}</h5>
                  <Button variant="ghost" size="icon" onClick={openEditTestCase} className="shrink-0 text-gray-500 hover:text-blue-600">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <TestCaseImportModal onDone={onImportQuestions} />
                  <TestCaseExportModal testcase={selectedTestCase} />
                  <Button onClick={runTC} size="sm" className="bg-green-600 hover:bg-green-700 text-white"><Play className="w-3 h-3 mr-2" /> <Trans>Run Test</Trans></Button>
                </div>
              </div>

              <div className="flex items-end gap-2 mt-4 bg-white p-3 border rounded shadow-sm">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="nq"><Trans>New Question</Trans></Label>
                  <Input
                    id="nq"
                    placeholder={t`Enter a question`}
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={e => UtilApp.runOnEnter(e, addQuestion)}
                  />
                </div>
                <Button onClick={addQuestion} disabled={!question}><Trans>Add</Trans></Button>
              </div>
            </div>

            <div className="p-3 bg-gray-50 text-xs font-bold text-gray-500 uppercase border-b"><Trans>Test questions</Trans></div>

            <ScrollArea className="flex-1 bg-white">
              <div className="divide-y">
                {selectedTestCase.questions.length === 0 && <div className="p-4 text-center text-gray-500 text-sm"><Trans>No questions to run. Add a new question.</Trans></div>}
                {selectedTestCase.questions.map((q, i) => (
                  <div key={i} className="p-3 flex items-start justify-between hover:bg-gray-50 group">
                    <div className="text-sm text-gray-800 pr-2">{q.question}</div>
                    <Button variant="ghost" size="icon" className="shrink-0 h-6 w-6 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600" onClick={() => openEditQuestion(q)}>
                      <Edit2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <div id='scroll-here-on-add' />
              </div>
            </ScrollArea>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400">
            <Trans>Select a test case to edit</Trans>
          </div>
        )}
      </div>

      {/* Add Test Case Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Trans>Add test case</Trans></DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label><Trans>Title</Trans></Label>
              <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder={t`Title`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}><Trans>Cancel</Trans></Button>
            <Button onClick={handleAddTestCase} disabled={!newTitle}><Trans>Add</Trans></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Test Case Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Trans>Edit test case</Trans></DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label><Trans>Title</Trans></Label>
              <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder={t`Title`} />
            </div>
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="destructive" onClick={handleDeleteTestCase}><Trans>Delete</Trans></Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialog(prev => ({ ...prev, open: false }))}><Trans>Cancel</Trans></Button>
              <Button onClick={handleSaveTestCase} disabled={!newTitle}><Trans>Save</Trans></Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={editQuestionDialog.open} onOpenChange={(open) => setEditQuestionDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Trans>Edit question</Trans></DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label><Trans>Question</Trans></Label>
              <Input value={editQuestionDialog.question} onChange={e => setEditQuestionDialog(prev => ({ ...prev, question: e.target.value }))} placeholder={t`Question`} />
            </div>
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="destructive" onClick={handleDeleteQuestion}><Trans>Delete</Trans></Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditQuestionDialog(prev => ({ ...prev, open: false }))}><Trans>Cancel</Trans></Button>
              <Button onClick={handleSaveQuestion} disabled={!editQuestionDialog.question}><Trans>Save</Trans></Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={alert.open} onOpenChange={(open) => setAlert(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alert.title}</AlertDialogTitle>
            <AlertDialogDescription>{alert.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {alert.confirm ?
              <>
                <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                <AlertDialogAction onClick={() => { alert.confirm?.(); setAlert(prev => ({ ...prev, open: false })) }}><Trans>Confirm</Trans></AlertDialogAction>
              </> :
              <AlertDialogAction onClick={() => setAlert(prev => ({ ...prev, open: false }))}><Trans>OK</Trans></AlertDialogAction>
            }
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  function TestCaseItem({ tc }: { tc: SoribotTestCase }) {
    const isSelected = selectedTestCase?.id === tc.id;
    return <div
      onClick={() => changeSelectedTestCase(tc)}
      className={`p-3 cursor-pointer text-sm hover:bg-gray-100 transition-colors ${isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : 'border-l-4 border-transparent'}`}
    >
      <div className="truncate font-medium">{tc.title}</div>
    </div>
  }

  function handleAddTestCase() {
    if (newTitle) {
      Fire.bot.addTestCase(bot.id, newTitle).then(tc => {
        setTestCases([tc, ...testCases]);
        changeSelectedTestCase(tc);
        setAddDialog(false);
      })
    }
  }

  function openEditTestCase() {
    if (!selectedTestCase) return;
    setNewTitle(selectedTestCase.title);
    setEditDialog({ open: true, title: selectedTestCase.title, tc: selectedTestCase });
  }

  function handleSaveTestCase() {
    if (newTitle && editDialog.tc) {
      editDialog.tc.title = newTitle;
      setInProgress(true);
      Fire.bot.saveTestCase(editDialog.tc).then(() => {
        setInProgress(false);
        changeSelectedTestCase(editDialog.tc!);
        setEditDialog(prev => ({ ...prev, open: false }));
      });
    }
  }

  function handleDeleteTestCase() {
    if (editDialog.tc) {
      setInProgress(true);
      Fire.bot.deleteTestCase(editDialog.tc).then(() => {
        setInProgress(false);
        setSelectedTestCase(undefined);
        setEditDialog(prev => ({ ...prev, open: false }));
      });
    }
  }

  function changeSelectedTestCase(tc: SoribotTestCase) {
    SelectedTestCase = tc;
    setSelectedTestCase(tc);
  }

  async function addQuestion() {
    if (!selectedTestCase || !question) return;

    selectedTestCase.questions.push({ question: question, evaluationMethod: 'auto' });
    setInProgress(true);
    setQuestion('');
    await Fire.bot.saveTestCase(selectedTestCase);
    setSelectedTestCase(selectedTestCase); // Trigger re-render (mutation approach)
    // Ideally we should clone it: setSelectedTestCase({...selectedTestCase});
    setInProgress(false);
    scrollToBottom();
  }

  function openEditQuestion(q: TestQuestion) {
    setEditQuestionDialog({ open: true, question: q.question, qObj: q });
  }

  function handleSaveQuestion() {
    if (editQuestionDialog.qObj && editQuestionDialog.question && selectedTestCase) {
      editQuestionDialog.qObj.question = editQuestionDialog.question;
      setInProgress(true);
      Fire.bot.saveTestCase(selectedTestCase).then(() => {
        setInProgress(false);
        setSelectedTestCase({ ...selectedTestCase });
        setEditQuestionDialog(prev => ({ ...prev, open: false }));
      });
    }
  }

  function handleDeleteQuestion() {
    if (editQuestionDialog.qObj && selectedTestCase) {
      selectedTestCase.questions = selectedTestCase.questions.filter(x => x.question !== editQuestionDialog.qObj!.question);
      setInProgress(true);
      Fire.bot.saveTestCase(selectedTestCase).then(() => {
        setInProgress(false);
        setSelectedTestCase({ ...selectedTestCase });
        setEditQuestionDialog(prev => ({ ...prev, open: false }));
      });
    }
  }

  async function runTC() {
    if (!selectedTestCase) return;

    try {
      setInProgress(true);
      const r = await Fire.bot.runTestCase({ soribotId: selectedTestCase.soribotId, testCaseId: selectedTestCase.id });
      if (r.error) {
        setAlert({ open: true, title: t`Error`, message: r.error, confirm: null });
      } else {
        history.replace(`${Page.soribot}/testruns/${bot.id}`)
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: error.message });
    } finally {
      setInProgress(false);
    }
  }

  async function onImportQuestions(questions: string[]) {
    if (!selectedTestCase) return;
    selectedTestCase.questions = (selectedTestCase.questions ?? []).concat(questions.map(q => new TestQuestion(q, 'auto')));
    setInProgress(true);
    await Fire.bot.saveTestCase(selectedTestCase);
    setInProgress(false);
    scrollToBottom();
  }

  function scrollToBottom() {
    setTimeout(() => {
      const el = document.getElementById(`scroll-here-on-add`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }
}

export default SoribotTestCases;